import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { TerraformAPI } from "../../../../utils/axios";
import {
  Message,
  UserMessage,
  AssistantMessage,
  StreamEvent,
  TerraformFile,
  CostSummary,
  RightPanelState,
  ResourceConfig,
  ToolCall,
} from "../types";

/* -------------------------------------------------------------------------------------------------
 * Hook types
 * -----------------------------------------------------------------------------------------------*/
interface UseStreamingChatProps {
  sessionId: string;
  token: string | null;
  selectedProject: string | null;
  onError: (error: string) => void;
  onNavigate: (path: string, state?: unknown) => void;
}

interface StreamingChatState {
  messages: Message[];
  isMessageSending: boolean;
  // Legacy fields kept to avoid breaking the surrounding UI.
  terraformFiles: TerraformFile[];
  costSummary: CostSummary | null;
  rightPanelState: RightPanelState;
  resourceConfig: ResourceConfig;
}

/* -------------------------------------------------------------------------------------------------
 * useStreamingChat – simplified to support the new streaming event contract
 * -----------------------------------------------------------------------------------------------*/
export const useStreamingChat = ({
  sessionId,
  token,
  selectedProject,
  onError,
  onNavigate,
}: UseStreamingChatProps) => {
  const queryClient = useQueryClient();

  /* ---------------------------------------------------------------------------
   * Local state
   * -------------------------------------------------------------------------*/
  const [messageIdCounter, setMessageIdCounter] = useState(0);
  const [state, setState] = useState<
    StreamingChatState & { activeToolCalls: Map<string, ToolCall> }
  >({
    messages: [],
    isMessageSending: false,
    terraformFiles: [],
    costSummary: null,
    rightPanelState: "empty",
    resourceConfig: {},
    activeToolCalls: new Map(),
  });

  /* ---------------------------------------------------------------------------
   * Helper – message factories
   * -------------------------------------------------------------------------*/
  const createUserMessage = useCallback(
    (content: string): UserMessage => {
      const id = `user_${Date.now()}_${messageIdCounter}`;
      setMessageIdCounter((prev) => prev + 1);
      return {
        id,
        type: "human",
        content,
        timestamp: Date.now(),
      };
    },
    [messageIdCounter]
  );

  const createAssistantMessage = useCallback(
    (content: string, loading = true): AssistantMessage => ({
      id: `assistant_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: "assistant",
      content,
      timestamp: Date.now(),
      loading,
    }),
    []
  );

  /* ---------------------------------------------------------------------------
   * Helper – update messages atomically
   * -------------------------------------------------------------------------*/
  const updateMessages = useCallback(
    (updater: (prev: Message[]) => Message[]) => {
      setState((prev) => ({ ...prev, messages: updater(prev.messages) }));
    },
    []
  );

  /* ---------------------------------------------------------------------------
   * Helper – find last index compat
   * -------------------------------------------------------------------------*/
  const findLastIndexCompat = <T>(
    arr: T[],
    predicate: (value: T) => boolean
  ): number => {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (predicate(arr[i])) return i;
    }
    return -1;
  };

  /* ---------------------------------------------------------------------------
   * Stream processing according to the new contract (assistant/tool_call)
   * -------------------------------------------------------------------------*/
  const processStreamEvent = useCallback(
    (
      evt: StreamEvent & {
        type?: string;
        chunk?: any;
        nodes?: any;
        edges?: any;
        total_monthly_cost?: any;
        past_total_monthly_cost?: any;
        diff?: any;
        resource_costs?: any;
      }
    ) => {
      const eventType =
        (evt as any).event_type ??
        (evt.role as string | undefined) ??
        evt.type ??
        "";

      switch (eventType) {
        case "assistant": {
          const token = evt.content ?? "";
          updateMessages((prev) => {
            // Ignore JSON fragments that are only tool_args remnants
            if (token.includes('"tool_args"')) {
              return prev; // skip
            }

            // Try to append to the last *loading* assistant message
            const lastIdx = findLastIndexCompat(
              prev,
              (m) => m.type === "assistant" && (m as AssistantMessage).loading
            );

            if (lastIdx !== -1) {
              const updated = [...prev];
              const last = updated[lastIdx] as AssistantMessage;
              const prevCharMatch = last.content.match(/[^\s]$/);
              const prevChar = prevCharMatch ? prevCharMatch[0] : "";
              const needsSpace =
                Boolean(prevChar) &&
                /[.,;:!?]/.test(prevChar) &&
                !/^\s|^\n/.test(token);
              const spacer = needsSpace ? " " : "";
              updated[lastIdx] = {
                ...last,
                content: last.content + spacer + token,
              };
              return updated;
            }

            // Otherwise, start a new assistant message
            return [...prev, createAssistantMessage(token, true)];
          });
          break;
        }

        case "tool_call": {
          // Render as progress card
          const toolId = `tc_${Date.now()}_${Math.random()}`;
          const toolCall: ToolCall = {
            id: toolId,
            toolName: evt.content?.split(" ")[0] || "tool",
            status: "completed", // we treat single event as completed
            args: {},
            startTime: evt.timestamp,
            endTime: evt.timestamp,
            details: evt.content,
          };

          setState((prev) => {
            const newMap = new Map(prev.activeToolCalls);
            newMap.set(toolId, toolCall);

            return { ...prev, activeToolCalls: newMap };
          });

          const cardMessage: AssistantMessage = {
            id: `assistant_${toolId}`,
            type: "assistant",
            content: "", // cards don't need text
            timestamp: Date.now(),
            loading: false,
            eventType: "tool_call",
            toolCalls: [toolCall],
          };

          updateMessages((prev) => [...prev, cardMessage]);
          break;
        }

        case "assistant_done": {
          const backendId =
            (evt as any).metadata?.message_id ?? (evt as any).message_id;

          if (!backendId) break; // nothing to do if server didn't send id

          updateMessages((prev) => {
            // Find the last assistant message that is still loading;
            const lastIdx = findLastIndexCompat(
              prev,
              (m) => m.type === "assistant" && (m as AssistantMessage).loading
            );

            if (lastIdx === -1) return prev;

            const updated = [...prev];
            const last = updated[lastIdx] as AssistantMessage;
            updated[lastIdx] = {
              ...last,
              backendId, // store UUID for ChatFeedback to use
              loading: false, // mark as finished so UI can show feedback icons
            } as AssistantMessage;

            return updated;
          });
          break;
        }

        case "error": {
          onError(evt.content ?? "Unknown error");
          updateMessages((prev) => [
            ...prev,
            createAssistantMessage(`Error: ${evt.content}`, false),
          ]);
          break;
        }

        case "terraform_root_file":
        case "terraform_module_file":
        case "terraform_documentation_file":
        case "terraform_file": {
          // New protocol: file content in evt.content, metadata houses filename etc.
          let fileObj: any;
          if ((evt as any).chunk) {
            // Legacy path – keep existing handling
            const chunk = (evt as any).chunk;
            fileObj = { ...chunk, type: "terraform_file" };
          } else {
            fileObj = {
              ...(evt as any).metadata,
              content: (evt as any).content,
              type: "terraform_file",
            };
          }
          setState((prev) => ({
            ...prev,
            terraformFiles: [...prev.terraformFiles, fileObj],
            rightPanelState: "terraform",
          }));
          break;
        }

        case "terraform_mermaid": {
          // treat as a special file so WorkspacePanel can show graph
          const meta = (evt as any).metadata ?? {};
          const mermaidObj = {
            type: "terraform_mermaid",
            nodes: meta.nodes ?? (evt as any).nodes ?? [],
            edges: meta.edges ?? (evt as any).edges ?? [],
          } as any;
          setState((prev) => ({
            ...prev,
            terraformFiles: [...prev.terraformFiles, mermaidObj],
            rightPanelState: "terraform",
          }));
          break;
        }

        case "cost_summary": {
          const summary = (evt as any).metadata ?? evt;
          setState((prev) => ({
            ...prev,
            costSummary: {
              total_monthly_cost: summary.total_monthly_cost,
              past_total_monthly_cost: summary.past_total_monthly_cost,
              diff: summary.diff,
              resource_costs: summary.resource_costs,
            },
            rightPanelState: "content",
          }));
          break;
        }

        default:
          // Ignore any other event types for now
          break;
      }
    },
    [createAssistantMessage, onError, updateMessages, findLastIndexCompat]
  );

  /* ---------------------------------------------------------------------------
   * Mutation – send a new user message & handle SSE stream
   * -------------------------------------------------------------------------*/
  const sendMessage = useMutation({
    mutationKey: ["sendMessage", sessionId, selectedProject],
    mutationFn: async ({
      message,
      files,
      options,
    }: {
      message: string;
      files?: File[];
      options?: { addUserMessage?: boolean };
    }) => {
      if (!token) {
        throw new Error("Authentication token missing. Please log in again.");
      }

      // 1) Optimistically add the user message if requested
      if (options?.addUserMessage !== false) {
        const userMsg = createUserMessage(message);
        updateMessages((prev) => [...prev, userMsg]);
      }
      setState((prev) => ({ ...prev, isMessageSending: true }));

      // TODO: When backend supports file uploads, modify the request to include files
      // For now, we could append file names to the prompt as a temporary solution
      let finalPrompt = message;
      if (files && files.length > 0) {
        const fileNames = files.map((f) => f.name).join(", ");
        finalPrompt = `${message}\n\n[Attached files: ${fileNames}]`;
        console.warn(
          "File upload not yet supported by backend. File names appended to prompt."
        );
      }

      // 2) Call backend & stream events
      const response = await TerraformAPI.generateConfig({
        prompt: finalPrompt,
        conversation_id: sessionId,
        token,
        project_id:
          selectedProject && !Number.isNaN(Number(selectedProject))
            ? selectedProject
            : "",
      });

      const stream = await response.stream();

      for await (const chunk of stream) {
        const lines = chunk.split("\n").filter((l) => l.trim());
        for (const line of lines) {
          // Backend may prefix with "data:" or send raw JSON – support both.
          const jsonStr = line.startsWith("data:")
            ? line.slice(5).trim()
            : line;
          if (!jsonStr) continue;
          try {
            const evt: StreamEvent = JSON.parse(jsonStr);
            processStreamEvent(evt);
          } catch (err) {
            console.error("Failed to parse SSE event:", err, jsonStr);
          }
        }
      }

      // 3) Mark any streaming assistant messages as complete
      updateMessages((prev) =>
        prev.map((m) =>
          m.type === "assistant"
            ? ({
                ...(m as AssistantMessage),
                loading: false,
              } as AssistantMessage)
            : m
        )
      );

      return { success: true };
    },
    onError: (err) => {
      setState((prev) => ({ ...prev, isMessageSending: false }));

      if (isAxiosError(err) && err.response?.status === 401) {
        onError("Authentication failed. Please login again.");
        onNavigate("/login");
        return;
      }

      onError("Oops, we ran into an error. Please try again later.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["sendMessage", sessionId, selectedProject],
      });
      setState((prev) => ({ ...prev, isMessageSending: false }));
    },
  });

  /* ---------------------------------------------------------------------------
   * Public helpers
   * -------------------------------------------------------------------------*/
  const resetStreamingState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      terraformFiles: [],
      costSummary: null,
      rightPanelState: "empty",
      resourceConfig: {},
      activeToolCalls: new Map(),
    }));
  }, []);

  // Wrapper function to maintain backward compatibility
  const sendMessageWrapper = useCallback(
    (
      message: string,
      files?: File[],
      options?: { addUserMessage?: boolean }
    ) => {
      sendMessage.mutate({ message, files, options });
    },
    [sendMessage]
  );

  /* ---------------------------------------------------------------------------
   * Exposed API
   * -------------------------------------------------------------------------*/
  return {
    ...state,
    sendMessage: sendMessageWrapper,
    isLoading: sendMessage.isPending,
    updateMessages,
    resetStreamingState,
  };
};
